import { Component, Input, ViewChild } from '@angular/core';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SitesService } from 'src/app/sites/sites.service';
import { DomainsService } from 'src/app/shared/services/domains.service';
import { Router } from '@angular/router';

type Group = {
	id: number;
	name: string;
	devices: Array<{
		macAddress: string;
		serialNumber: string;
		events: Array<{
			sequence_id: number;
			event_type: string;
			calc_duration: number;
			end_soc: number;
		}>;
		battery_connected: boolean;
	}>;
};

@Component({
	selector: 'app-brm-devices-simulation',
	templateUrl: './brm-devices-simulation.component.html',
	styleUrls: ['./brm-devices-simulation.component.css']
})
export class BrmDevicesSimulationComponent {
	@Input() siteId: number;
	@ViewChild("addEventsPopup") addEventsPopup: ModalComponent;
	@ViewChild("editEventPopup") editEventPopup: ModalComponent;

	groups: Group[] = [];
	selectedDevice = null;
	selectedGroupIndex = 0;
	newEvent = {
		event_type: '',
		calc_duration: 0,
		end_soc: 0,
	}
	insertEventsError = '';
	eventTypes =  [
		{ value: 1, label: this.translateService.instant('events.event_type_1') },
		{ value: 2, label: this.translateService.instant('events.event_type_2') },
		{ value: 3, label: this.translateService.instant('events.event_type_3') },
		{ value: null, label: this.translateService.instant('events.event_type_off') },
	];

	editEventError = '';
	editEvent = {
		index: null,
		macAddress: null,
		groupIndex: null,
		event: {
			sequence_id: null,
			event_type: '',
			calc_duration: 0,
			end_soc: 0,
		}
	};

	constructor(
		private siteService: SitesService,
		private translateService: TranslateService,
		private notificationMessage: NotificationMessageService,
		private domainsService: DomainsService,
		private router: Router,
	) {}


	ngOnInit() {
		const isProduction = this.domainsService.isProductionDomain;
		if (isProduction) {
			this.router.navigate(['/unauthorized']);
			return;
		}

		this.getSimulationData();
	}

	getSimulationData() {
		this.siteService.getBrmSimulationData(this.siteId).subscribe((res: {groups: Group[], devicesEvents: any}) => {
			this.groups = res.groups;
			const devicesEvents = res.devicesEvents || {};

			// Assign device events to the respective devices
			this.groups.forEach(group => {
				group.devices.forEach(device => {
					if (devicesEvents[device.macAddress]) {
						device.events = devicesEvents[device.macAddress].map((event, index) => ({
							sequence_id: event.sequence_id || index + 1,
							event_type: event.event_type,
							calc_duration: event.calc_duration,
							end_soc: event.end_soc
						}));
					} else {
						device.events = [];
					}

					device.events.sort((a, b) => b.sequence_id - a.sequence_id);
				});
			});
		});
	}

	showAddEventsPopup(macAddress: string, groupIndex: number) {
		this.selectedDevice = macAddress;
		this.selectedGroupIndex = groupIndex;

		this.newEvent = {
			event_type: '',
			calc_duration: 0,
			end_soc: 0,
		};

		this.addEventsPopup.show();
	}

	clearDeviceEvents(macAddress: string, groupIndex: number) {
		const device = this.groups[groupIndex].devices.find(device => device.macAddress === macAddress);
		if (device) {
			device.events = [];
		}
	}

	addEvent(addMore) {
		this.insertEventsError = '';
		if (this.newEvent.calc_duration > 24 * 60 * 60) {
			this.insertEventsError = this.translateService.instant('brm_devices_simulation.error_duration_exceeded');
			return;
		}

		if (this.newEvent.calc_duration <= 0) {
			this.insertEventsError = this.translateService.instant('brm_devices_simulation.duration_should_be_positive');
			return;
		}

		if (this.newEvent.end_soc < 0 || this.newEvent.end_soc > 100) {
			this.insertEventsError = this.translateService.instant('brm_devices_simulation.error_soc_out_of_range');
			return;
		}

		if (this.newEvent.event_type == '') {
			this.insertEventsError = this.translateService.instant('brm_devices_simulation.error_event_type_required');
			return;
		}

		const device = this.groups[this.selectedGroupIndex].devices.find(device => device.macAddress === this.selectedDevice);
		device.events = device.events || [];

		device.events.push({
			sequence_id: device.events.length + 1,
			event_type: this.newEvent.event_type,
			calc_duration: this.newEvent.calc_duration,
			end_soc: this.newEvent.end_soc
		});

		// Sort events by sequence_id in descending order
		device.events.sort((a, b) => b.sequence_id - a.sequence_id);

		if (addMore) {
			this.newEvent = {
				event_type: '',
				calc_duration: 0,
				end_soc: 0,
			};
		}
		else {
			this.addEventsPopup.hide();
		}
	}

	clearAllEvents() {
		for (const group of this.groups) {
			for (const device of group.devices) {
				device.events = [];
			}
		}
	}

	deleteEvent(macAddress: string, eventIndex: number, groupIndex: number) {
		const device = this.groups[groupIndex].devices.find(device => device.macAddress === macAddress);
		if (device && device.events && device.events.length > eventIndex) {
			device.events.splice(eventIndex, 1);

			// Reorder sequence_id after deletion
			device.events.forEach((event, index) => {
				event.sequence_id = index + 1;
			});
		}
	}

	openEditEventPopup(macAddress: string, eventIndex: number, groupIndex: number) {
		const device = this.groups[groupIndex].devices.find(device => device.macAddress === macAddress);

		if (device && device.events && device.events.length > eventIndex) {
			this.editEvent = {
				index: eventIndex,
				macAddress: macAddress,
				groupIndex: groupIndex,
				event: {
					sequence_id: device.events[eventIndex].sequence_id,
					event_type: device.events[eventIndex].event_type,
					calc_duration: device.events[eventIndex].calc_duration,
					end_soc: device.events[eventIndex].end_soc,
				}
			};
			this.editEventError = '';
			this.editEventPopup.show();
		}
	}

	editDeviceEvent() {
		this.editEventError = '';
		if (this.editEvent.event.calc_duration > 24 * 60 * 60) {
			this.editEventError = this.translateService.instant('brm_devices_simulation.error_duration_exceeded');
			return;
		}

		if (this.editEvent.event.calc_duration <= 0) {
			this.editEventError = this.translateService.instant('brm_devices_simulation.duration_should_be_positive');
			return;
		}

		if (this.editEvent.event.end_soc < 0 || this.editEvent.event.end_soc > 100) {
			this.editEventError = this.translateService.instant('brm_devices_simulation.error_soc_out_of_range');
			return;
		}

		if (this.editEvent.event.event_type == '') {
			this.editEventError = this.translateService.instant('brm_devices_simulation.error_event_type_required');
			return;
		}

		const device = this.groups[this.editEvent.groupIndex].devices.find(device => device.macAddress === this.editEvent.macAddress);
		if (device && device.events && device.events.length > this.editEvent.index) {
			device.events[this.editEvent.index] = {
				sequence_id: this.editEvent.event.sequence_id,
				event_type: this.editEvent.event.event_type,
				calc_duration: this.editEvent.event.calc_duration,
				end_soc: this.editEvent.event.end_soc
			};

			// Reorder sequence_id after editing
			device.events.forEach((event, index) => {
				event.sequence_id = index + 1;
			});
		}

		this.editEventPopup.hide();
		this.editEvent = {
			index: null,
			macAddress: null,
			groupIndex: null,
			event: {
				sequence_id: null,
				event_type: '',
				calc_duration: 0,
				end_soc: 0,
			}
		};
	}

	submit() {
		const devicesData = [];

		this.groups.forEach(group => {
			if (!group.devices || group.devices.length === 0)
				return;

			devicesData.push(...group.devices);
		});

		this.siteService.updateBrmSimulationData(this.siteId, devicesData).subscribe(res => {
			this.notificationMessage.setMessage('globalSuccessMsg');
		})
	}
}
